import { useState, useEffect } from 'react';
import { Bell, BellOff, CheckCircle } from 'lucide-react';
import {
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  checkPushSubscriptionStatus
} from '../lib/pushNotifications';

export default function NotificationPrompt() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setChecking(true);
    const status = await checkPushSubscriptionStatus();
    setIsSubscribed(status);
    setChecking(false);
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const subscription = await subscribeToPushNotifications();
      if (subscription) {
        setIsSubscribed(true);
      }
    } catch (error) {
      console.error('Failed to subscribe:', error);
      alert('Failed to enable notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      const success = await unsubscribeFromPushNotifications();
      if (success) {
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      alert('Failed to disable notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="bg-white rounded-xl p-4 border border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center animate-pulse">
            <Bell className="w-5 h-5 text-gray-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">Checking notification status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          isSubscribed ? 'bg-green-100' : 'bg-blue-100'
        }`}>
          {isSubscribed ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <Bell className="w-5 h-5 text-blue-600" />
          )}
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">
            {isSubscribed ? 'Notifications Enabled' : 'Push Notifications'}
          </p>
          <p className="text-xs text-gray-500">
            {isSubscribed
              ? 'You will receive health reminders and updates'
              : 'Get notified about your health progress'}
          </p>
        </div>
        <button
          onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
          disabled={loading}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            isSubscribed
              ? 'bg-red-100 text-red-700 hover:bg-red-200'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {loading ? 'Loading...' : isSubscribed ? 'Disable' : 'Enable'}
        </button>
      </div>
    </div>
  );
}
